import { Plugin, Context } from '@nuxt/types';
import { cXense } from '@analytics/trackers';
import { Data as ArticleData } from '@root/modules/article/types/article';
import cXensePageType from '@root/modules/analytics/config/cxense/cXensePageType.config';
import events from '../config/cxense';
import interpolate from '@root/common/utils/interpolate';
import getMobileApplication from '../utils/getMobileApplication';
import { CxenseEvent } from '@root/modules/analytics/types/analytics';
import getEventByKey from '../utils/getEventByKey';
import getDelfiHashTracker from '../utils/getDelfiHashTracker';

type CustomParameters = Record<string, unknown>;

interface Section {
  id: string;
  title: string;
}

interface Block {
  id: string;
  title: string;
}

const sectionIdMap: Section[] = [
  { id: '4c65b5a6-99ee-4764-abed-d3dbd6e3aa39', title: 'TOP' },
  { id: 'a92b5c45-5e8b-400c-ad9c-331cbaab9837', title: 'Slėptuvės ir priedangos' },
];

const blockIdMap: Block[] = [
  // TOP
  { id: 'c6844a88-73a3-4085-a518-225d988c6ea9', title: 'TOP 5' },
  { id: 'ce30f94f-bee6-481d-a08d-ed401b7e5619', title: 'TOP6-10' },
  { id: 'ddafc9e5-a0b3-4ede-b96e-7d824d3a877f', title: 'TOP 11-15' },
  { id: '57614eb4-399e-43d3-9065-7477c2a39cb5', title: 'TOP 16-20' },
  { id: '58494601-ecb6-4fb7-9c81-a14725bd5d73', title: 'TOP 21-23' },
  // Aktualijos
  { id: '410c2fc0-399a-439d-be82-179451e9fd76', title: 'Aktualijos TOP 5' },
  { id: 'c71012a2-6b5f-4973-a449-bdfa6befdd8b', title: 'Politiko akimis' },
  { id: 'c1ac5479-ce36-4d2f-acc5-dc2e3f685dbd', title: 'Aktualijos TOP 6-10' },
  { id: '2cf718e2-68fb-4ba8-9b88-ad5f2ea18a13', title: 'ru.delfi.lt' },
  { id: 'a034cde4-a46c-400e-bea4-b2c0f86c8901', title: 'Aktualijos TOP 11-17' },
  { id: '6fc8d6b4-94ec-48c1-95b1-29091661dc0e', title: 'Aktualijos TOP 18-22' },
  { id: '6dbe5924-d79b-450e-ad6b-c82f5917b64b', title: 'Aktualijos TOP 23-26' },
  { id: '05281cc5-82a6-4452-b47f-434fc480dda9', title: 'Aktualijos Užsakomasis turinys' },
  { id: '1ce5da63-abfc-495b-a487-792aab951649', title: 'Aktualijos TOP 27-31' },
];

interface EventParams {
  section: string;
  sectionIndex: string;
  sectionName?: string;
  blockId?: string;
  blockName?: string;
}

const cXensePlugin: Plugin = async (ctx) => {
  const { app, store, route } = ctx;
  const analytics = app.$channelConfig('analytics');
  const { id, locale } = app.$channelConfig('settings');

  if (!analytics?.cxense?.siteId) {
    return;
  }

  const cX = new cXense();
  await cX.init({ uid: analytics.cxense.siteId });

  cX.push('cint', '224');
  cX.push('sync', 'adform');

  const pageCustomParameters = () => {
    const routeName = app.router?.currentRoute.name;
    const hash = window.location.hash;
    const customParameters: CustomParameters = {};
    customParameters.channel = id;

    const hashParameter = getDelfiHashTracker(hash);
    if (hashParameter) {
      customParameters.delfi_link = hashParameter;
    }

    customParameters.page_type = cXensePageType[routeName || 'other'] || cXensePageType['other'];
    customParameters.user_logged_in = store.state.piano.isLoggedIn ? 'yes' : 'no';
    customParameters.reg_user_dplus = store.state.piano.access.channelAccess;
    customParameters.ad_block = store.state.adForm.enableAdBlockLayer;

    const application = getMobileApplication(window.navigator.userAgent);
    if (application) {
      customParameters.browser_container = application;
    }

    if (store.state.piano.profile) {
      customParameters.userId = store.state.piano.profile?.uid;
    }

    return customParameters;
  };

  const cXensePageView = (route: Context['route']) => {
    const isArticle = route.name === 'article';

    // Send page view of all pages except article
    if (isArticle) {
      return false;
    }

    const customParameters = pageCustomParameters();

    cX.pageView({}, customParameters);
  };

  app?.router?.afterEach((to) => {
    cXensePageView(to);
  });

  // Get cXense segments and share them to trackers
  window.addEventListener('load', async () => {
    const userId = await cX.getUserId();
    cX.push('getUserSegmentIds', {
      persistedQueryId: 'b04a959c2ea64c1ef725a6abb04ccecbbd9e6671',
      callback: async (segmentIds: string[]) => {
        store.commit('analytics/setClickEvent', {
          facebookPixel: {
            eventName: 'SYNC_SEGMENTS',
            eventData: { eventType: 'sync', data: { type: 'trackCustom', name: 'CxSegments', segmentIds } },
          },
          getSiteControl: {
            eventName: 'SYNC_SEGMENTS',
            eventData: { eventType: 'sync', data: { segmentIds, userId } },
          },
        });
      },
    });
  });

  const cXenseArticlePageView = (activeArticle: ArticleData) => {
    const { channelAccess } = store.state.piano.access;

    let customParameters = pageCustomParameters();
    const articleCustomParameters: CustomParameters = {
      channel: activeArticle.primaryCategory.channel.path ?? 'delfi',
      is_paid_article: activeArticle.content.paywall.enabled,
      is_paid_article_and_have_paid: activeArticle.content.paywall.enabled && channelAccess,
    };

    customParameters = { ...customParameters, ...articleCustomParameters };
    cX.pageView({}, customParameters);
  };

  const continuousReadingDepth = (activeArticle: ArticleData) => {
    const id = activeArticle.id;
    const crArticles = store.state.article.crArticles;
    let crDepth = 1;

    if (crArticles.length > 0 && crArticles.indexOf(id) !== -1) {
      crDepth = crArticles.indexOf(id) + 2;
    }

    cX.sendEvent('continuous-reading-depth', { value: crDepth }, { origin: 'lth-delfi', persistedQueryId: '60134f11dac530e2cd5c308a42f829d82289a3b8' });
  };

  // Watch activeArticle store state to send article page view
  store.watch(
    (state) => state.article.activeArticlePath,
    async (activeArticlePath) => {
      if (activeArticlePath && store.state.article.activeArticle) {
        const cXenseArticle = { ...store.state.article.activeArticle };
        cXenseArticlePageView(cXenseArticle);
        continuousReadingDepth(cXenseArticle);
      }
    }
  );

  if (store.state.piano.isScriptInited) {
    if (store.state.article.activeArticle) {
      const cXenseArticle = { ...store.state.article.activeArticle };
      cXenseArticlePageView(cXenseArticle);
      continuousReadingDepth(cXenseArticle);
    } else {
      cXensePageView(route);
    }
  } else {
    store.watch(
      (state) => state.piano.isScriptInited,
      async () => {
        if (store.state.article.activeArticle) {
          const cXenseArticle = { ...store.state.article.activeArticle };
          cXenseArticlePageView(cXenseArticle);
          continuousReadingDepth(cXenseArticle);
        } else {
          cXensePageView(route);
        }
      }
    );
  }

  // Watch analytics store eventName change
  store.watch(
    (state) => state.analytics.tracker.cXense.clickEventName,
    async (clickEventName) => {
      if (!clickEventName) {
        return;
      }
      const event = getEventByKey<CxenseEvent>(events, clickEventName, locale);

      if (event) {
        if (!store.state.analytics.tracker.cXense.clickEventData) {
          return false;
        }

        const { type, data } = store.state.analytics.tracker.cXense.clickEventData;
        const eventData = event.types[type];
        const { eventType, customParams, options } = eventData;
        const eventCustomParams = customParams ? JSON.parse(interpolate(customParams, { data, app })) : '';
        cX.sendEvent(eventType, eventCustomParams, options ? JSON.parse(options) : null);
      }
      store.commit('analytics/setClickEvent', { cXense: { eventName: null, eventData: null } });
    }
  );

  // Log sponsored content articles in view
  if (process.client && 'IntersectionObserver' in window && route.name === 'frontpage') {
    const elemsToObserve = document.querySelectorAll('.pt-content');
    const ptObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          const element = entry.target as unknown as HTMLElement;
          const articleUrl = element.querySelector('a')?.href as string;
          const urlParts = articleUrl.split('-') as string[];
          const articleId = urlParts[urlParts?.length - 1];

          cX.sendEvent(
            'article_visible',
            { article_id: articleId, article_url: articleUrl },
            { origin: 'lth-delfi', persistedQueryId: '60134f11dac530e2cd5c308a42f829d82289a3b8' }
          );

          ptObserver.unobserve(entry.target);
        }
      });
    }, {});

    elemsToObserve.forEach((elemToObserve) => {
      ptObserver.observe(elemToObserve);
    });

    const lazySections = document.querySelectorAll('.section-lazy');
    const lazyObserver = new MutationObserver((mutations) => {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
          Array.prototype.filter.call(mutation.addedNodes, (node) => {
            if (node.childNodes.length > 0) {
              const elemsToObserve = node.querySelector('.pt-content');
              if (elemsToObserve) {
                const elemsToObserve: Element[] = node.querySelectorAll('.pt-content');
                elemsToObserve.forEach((elemToObserve) => {
                  ptObserver.observe(elemToObserve);
                });
              }
            }
          });
        }
      });
    });

    lazySections.forEach((elemToObserve) => {
      lazyObserver.observe(elemToObserve, { childList: true, subtree: true });
    });
  }

  // Log frontpage sections visible event (scroll depth)
  if (process.client && 'IntersectionObserver' in window && route.name === 'frontpage' && route.path === '/') {
    const elemsToObserve = document.querySelectorAll('section');
    const sectionsObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          const element = entry.target as unknown as HTMLElement;

          // Get section name from BlockType8 or from sectionIdMap as fallback
          let sectionName = null;
          const sectionIndex = element.dataset.sectionIndex as string;
          const title = element.querySelector('.block-type-8');
          const heading = title?.querySelector('.block-type-8__title');
          if (heading) {
            sectionName = (heading as HTMLElement).innerText;
          } else {
            const sectionId = element.dataset.sectionId;
            if (sectionId) {
              const sectionIdMapEntry = sectionIdMap.find((entry) => entry.id === sectionId);
              if (sectionIdMapEntry) {
                sectionName = sectionIdMapEntry.title;
              }
            }
          }

          const params: EventParams = { section: `${sectionIndex}. ${sectionName}`, sectionIndex };

          if (sectionName) {
            params.sectionName = sectionName;
          }

          if (sectionName) {
            cX.sendEvent('section_visible', params, { origin: 'lth-delfi', persistedQueryId: '60134f11dac530e2cd5c308a42f829d82289a3b8' });
          }

          sectionsObserver.unobserve(entry.target);
        }
      });
    }, {});

    elemsToObserve.forEach((elemToObserve) => {
      sectionsObserver.observe(elemToObserve);
    });

    const lazyObserver = new MutationObserver((mutations) => {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
          Array.prototype.filter.call(mutation.addedNodes, (node) => {
            if (node.childNodes.length > 0 && node.nodeName === 'DIV' && node.classList.contains('row')) {
              const row = node as HTMLElement;
              const section = row.querySelector('section');
              if (section) {
                sectionsObserver.observe(section);
              }
            }
          });
        }
      });
    });

    const container = document.querySelectorAll('.container.default')[0];
    lazyObserver.observe(container, { childList: true, subtree: true });
  }

  // Log frontpage block articles clicks
  if (process.client && route.name === 'frontpage' && route.path === '/') {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const link = target?.closest('.section article a') as HTMLAnchorElement;

      if (!link) {
        return;
      }

      // Disable navagation to ensure event will be sent suceesfully
      event.preventDefault();

      const section = target?.closest('.section') as HTMLElement;
      const sectionIndex = section?.dataset.sectionIndex as string;

      // Get section name from BlockType8 or from sectionIdMap as fallback
      let sectionName = null;
      const title = section?.querySelector('.block-type-8');
      const heading = title?.querySelector('.block-type-8__title');
      if (heading) {
        sectionName = (heading as HTMLElement).innerText;
      } else {
        const sectionId = section?.dataset.sectionId;
        if (sectionId) {
          const sectionIdMapEntry = sectionIdMap.find((entry) => entry.id === sectionId);
          if (sectionIdMapEntry) {
            sectionName = sectionIdMapEntry.title;
          }
        }
      }

      // Get block name from blockIdMap
      let blockName = null;
      const block = target?.closest('.headline-blocks[data-container="articles-container"]');
      const blockId = block?.id.replace('block-', '') as string;
      if (blockId) {
        const block = blockIdMap.find((entry) => entry.id === blockId);
        blockName = block?.title;
      }

      const params: EventParams = { section: `${sectionIndex}. ${sectionName}`, sectionIndex, blockId };

      if (sectionName) {
        params.sectionName = sectionName;
      }

      if (blockName) {
        params.blockName = blockName;
      }

      cX.sendEvent('section_click', params, { origin: 'lth-delfi', persistedQueryId: '60134f11dac530e2cd5c308a42f829d82289a3b8' });

      // Restore navigation after event is sent
      window.location.href = link.href;
    });
  }
};

export default cXensePlugin;
